import _ from 'underscore';
import {
    computed, ComputedRef, ref, Ref, unref, watch, WritableComputedRef,
} from 'vue';
import { ProductForHotelFilters } from '@ve/components/hotel/filters/common/store/types';
import { Meal } from '@ve/types';
import { wordEnding } from '@ve/services/textModifiers';

export default ({
                    selectedMeals,
                    mealsRepository,
                    products,
                }: {
    selectedMeals: Ref<number[]>,
    mealsRepository: Ref<Meal[]>,
    products: Ref<ProductForHotelFilters[]>,
}):{
    selectedMealsText: ComputedRef<string>,
    moreThanOneMealAvailableOrSelected: ComputedRef<boolean>,
    allMealsChecked: WritableComputedRef<boolean>,
    mealsReference: ComputedRef<Meal[]>,
} => {
    const currentMealsReference = computed(() => _.uniq(unref(products).map((product) => product.meal), (meal) => meal.id));

    const reservedMeals: Ref<Meal[]> = ref([]);
    watch(currentMealsReference, (mealsReference) => {
        reservedMeals.value = _.uniq(([] as Meal[]).concat(unref(reservedMeals), mealsReference), (meal) => meal.id);
    });

    const reservedMealsReference = computed(() => {
        const mealsValue = unref(selectedMeals);
        if (!mealsValue.length) return [];

        const reservedMealsReferenceValue: Meal[] = [];
        unref(selectedMeals).forEach((mealId) => {
            if (!_.findWhere(unref(currentMealsReference), { id: mealId })) {
                const reservedMeal = _.findWhere(unref(reservedMeals), { id: mealId });
                if (reservedMeal) {
                    reservedMealsReferenceValue.push(reservedMeal);
                }
            }
        });
        return reservedMealsReferenceValue;
    });

    const combinedMealsReference = computed(() => ([] as Meal[]).concat(unref(currentMealsReference), unref(reservedMealsReference)));

    const mealsReference = computed(() => {
        const mealsRepositoryData = unref(mealsRepository);
        if (!mealsRepositoryData?.length) return [];
        const mealsRepositoryById = _.indexBy(mealsRepositoryData, 'id');
        const resultMealsArray: Meal[] = [];
        const extendedMealTypes:{[key: number]: number} = {
            4: 3,
            6: 5,
            12: 2,
            13: 2,
            14: 7,
            15: 2,
            16: 5,
            17: 3,
        };
        unref(selectedMeals).forEach((mealId) => {
            if (!_.findWhere(unref(combinedMealsReference), { id: mealId })) {
                const mealIdFromExtendedMealType = extendedMealTypes[mealId] ?? null;
                if (mealIdFromExtendedMealType) {
                    resultMealsArray.push(mealsRepositoryById[mealIdFromExtendedMealType]);
                } else if (mealsRepositoryById[mealId]) {
                    resultMealsArray.push(mealsRepositoryById[mealId]);
                }
            }
        });
        unref(combinedMealsReference).forEach((meal) => {
            const mealIdFromExtendedMealType = extendedMealTypes[meal.id] ?? null;
            if (mealIdFromExtendedMealType) {
                resultMealsArray.push(mealsRepositoryById[mealIdFromExtendedMealType]);
            } else if (mealsRepositoryById[meal.id]) {
                resultMealsArray.push(mealsRepositoryById[meal.id]);
            }
        });
        return _.uniq(resultMealsArray);
    });

    const selectedMealsText = computed(() => {
        const selectedMealsValue = unref(selectedMeals);
        if (unref(selectedMealsValue).some((mealId) => _.findWhere(unref(mealsReference), { id: mealId }))) {
            if (selectedMealsValue.length === 1) {
                const selectedMeal = unref(mealsReference).find((meal) => meal.id === selectedMealsValue[0]);
                return `${selectedMeal?.name}`;
            }
            if (selectedMealsValue.length > 4) {
                return `${selectedMealsValue.length} тип${wordEnding(selectedMealsValue.length, ';а;ов')} питания`;
            }
            if (selectedMealsValue.length > 1) {
                return _.pluck(unref(mealsReference).filter((meal) => !!~selectedMealsValue.indexOf(meal.id)), 'code').join(', ');
            }
        }
        return 'Все типы питания';
    });

    const moreThanOneMealAvailableOrSelected = computed(() => unref(mealsReference).length >= 1);

    const allMealsChecked = computed({
        get: () => !unref(selectedMeals).length,
        set: (value) => {
            if (value) {
                selectedMeals.value = [];
            }
        },
    });

    return {
        selectedMealsText,
        moreThanOneMealAvailableOrSelected,
        allMealsChecked,
        mealsReference,
    };
};